import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Link2, Mail, MessageCircle, Wallet } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { PayLinkDialog } from "./PayLinkDialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtDateTime, fmtMoney, todayAthens } from "../../../lib/format";
import { errMsg } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Field, PageHead, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const PromiseDialog = ({ row, onClose, onSaved }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ promise_date: todayAthens(7), amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/collections/${encodeURIComponent(row.code)}/promise`, { promise_date: f.promise_date, amount: f.amount ? Number(String(f.amount).replace(",", ".")) : null, note: f.note || null });
      toast.success(t("x.coll.promiseSaved")); onSaved();
    } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="promise-dialog">
        <DialogHeader><DialogTitle>{t("x.coll.promiseSet")} · {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t("x.coll.promiseDate")}><DateField value={f.promise_date} onChange={(v) => setF((s) => ({ ...s, promise_date: v }))} testId="promise-date" /></Field>
          <Field label={t("x.coll.promiseAmount")}><input inputMode="decimal" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} className={input} data-testid="promise-amount" placeholder={fmtMoney(row.overdue)} /></Field>
          <Field label={t("x.coll.promiseNote")}><input value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} className={input} data-testid="promise-note" /></Field>
          <button type="button" disabled={busy || !f.promise_date} onClick={save} className={`${btnPrimary} w-full`} data-testid="promise-save">{t("x.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, onRemind, onPromise, onLink }) => {
  const { t } = useI18n();
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-4" data-testid={`collection-row-${r.code}`}>
      <div className="min-w-0 flex-1">
        <Link to={`/staff/customers/${encodeURIComponent(r.code)}`} className="font-semibold text-navy hover:underline">{r.name}</Link>
        <p className="text-xs text-slate-500">{t("x.coll.days", { n: r.days_overdue })} · {t("x.coll.invoices", { n: r.overdue_invoices })} · {r.sales_rep}</p>
        {r.promise && <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium text-brand" data-testid={`promise-badge-${r.code}`}><CalendarClock className="h-3.5 w-3.5" />{t("x.coll.promise")}: {fmtDate(r.promise.promise_date)}{r.promise.amount ? ` · ${fmtMoney(r.promise.amount)}` : ""}</p>}
        {r.last_reminder_at && <p className="mt-1 text-xs text-slate-400">{t("x.coll.lastReminder")}: {fmtDateTime(r.last_reminder_at)} ({r.last_reminder_channel})</p>}
      </div>
      <div className="text-right"><p className="text-lg font-bold tabular-nums text-rose-600" data-testid={`collection-overdue-${r.code}`}>{fmtMoney(r.overdue)}</p><p className="text-xs text-slate-500">{fmtMoney(r.balance)}</p></div>
      <div className="flex w-full gap-2 sm:w-auto">
        <button type="button" onClick={() => onRemind(r, "email")} className={`${btnGhost} flex-1 px-3`} data-testid={`remind-email-${r.code}`}><Mail className="h-4 w-4" />{t("x.coll.remindEmail")}</button>
        <button type="button" onClick={() => onRemind(r, "whatsapp")} className={`${btnGhost} flex-1 px-3`} data-testid={`remind-wa-${r.code}`}><MessageCircle className="h-4 w-4 text-brand" />{t("x.coll.remindWa")}</button>
        <button type="button" onClick={() => onPromise(r)} className={`${btnPrimary} flex-1 px-3`} data-testid={`promise-${r.code}`}><CalendarClock className="h-4 w-4" />{t("x.coll.promise")}</button>
        {onLink && <button type="button" onClick={() => onLink(r)} className={`${btnGhost} flex-1 px-3`} data-testid={`pay-link-${r.code}`}><Link2 className="h-4 w-4 text-[#0080FF]" />{t("x.pay.link")}</button>}
      </div>
    </li>
  );
};

export default function Collections() {
  const { t } = useI18n();
  const [sort, setSort] = useState("amount");
  const [promise, setPromise] = useState(null);
  const [link, setLink] = useState(null);
  const { user } = useAuth();
  const canLink = hasPerm(user, "payments:link");
  const r = useApi("collections", "/staff/collections", { sort }, { staleTime: 0 });
  const remind = async (row, channel) => {
    const win = channel === "whatsapp" ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post(`/staff/collections/${encodeURIComponent(row.code)}/reminder`, { channel });
      if (win && data.link) { win.location.href = data.link; toast.success(t("x.coll.waReady")); } else toast.success(t("x.coll.emailSent"));
      r.refetch();
    } catch (e) { if (win) win.close(); toast.error(errMsg(t, e)); }
  };
  return (
    <div className="space-y-5" data-testid="collections-page">
      <PageHead title={t("x.coll.title")} sub={t("x.coll.sub")}>
        {["amount", "days"].map((k) => <button key={k} type="button" className={chip(sort === k)} onClick={() => setSort(k)} data-testid={`collections-sort-${k}`}>{t(k === "amount" ? "x.coll.sortAmount" : "x.coll.sortDays")}</button>)}
      </PageHead>
      <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800" data-testid="collections-mock-note">{t("x.mocked")}: {t("x.coll.mockNote")}</p>
      {r.isLoading ? <SkeletonBlock className="h-64" /> : !r.data?.length ? <EmptyState icon={Wallet} title={t("x.coll.empty")} testId="collections-empty" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="collections-list">
          {r.data.map((row) => <Row key={row.code} r={row} onRemind={remind} onPromise={setPromise} onLink={canLink ? setLink : null} />)}
        </ul>
      )}
      {link && <PayLinkDialog row={link} onClose={() => setLink(null)} onSent={() => r.refetch()} />}
      {promise && <PromiseDialog row={promise} onClose={() => setPromise(null)} onSaved={() => { setPromise(null); r.refetch(); }} />}
    </div>
  );
}
